import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Blackout } from "../api.js";
import {
  addBlackout,
  deleteBlackout,
  fetchAvailability,
  fetchSettings,
  refreshCalendars,
  saveSettings,
  saveWorkingHours,
  type WorkingHourRange,
} from "../api.js";

/**
 * Hours, timezone, booking rules and blackouts (1q and 1r), plus a preview of
 * what a visitor would actually be offered.
 *
 * The preview matters more than it looks: every control here removes time, and
 * an owner has no other way to see the consequence of a change before somebody
 * books against it.
 */

const DAYS = [
  { weekday: 1, label: "Mon" },
  { weekday: 2, label: "Tue" },
  { weekday: 3, label: "Wed" },
  { weekday: 4, label: "Thu" },
  { weekday: 5, label: "Fri" },
  { weekday: 6, label: "Sat" },
  { weekday: 7, label: "Sun" },
];

const DURATION_CHOICES = [15, 30, 45, 60, 90];

function toTimeValue(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

function fromTimeValue(value: string): number {
  const [hours, minutes] = value.split(":").map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

export function Availability() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ["settings"], queryFn: fetchSettings });
  const [hours, setHours] = useState<WorkingHourRange[]>([]);
  const [hoursError, setHoursError] = useState<string | null>(null);
  const [previewDuration, setPreviewDuration] = useState(30);

  useEffect(() => {
    if (settings.data) setHours(settings.data.workingHours);
  }, [settings.data]);

  const detectedZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["settings"] });
    void queryClient.invalidateQueries({ queryKey: ["availability"] });
  };

  const patch = useMutation({ mutationFn: saveSettings, onSuccess: invalidate });
  const putHours = useMutation({
    mutationFn: saveWorkingHours,
    onSuccess: () => {
      setHoursError(null);
      invalidate();
    },
    onError: (error: Error) => setHoursError(error.message),
  });
  const removeBlackout = useMutation({
    mutationFn: deleteBlackout,
    onSuccess: invalidate,
  });
  const refresh = useMutation({ mutationFn: refreshCalendars, onSuccess: invalidate });

  const availability = useQuery({
    queryKey: ["availability", previewDuration],
    queryFn: () => fetchAvailability(previewDuration, 7),
  });

  const booking = settings.data?.bookingSettings;
  const zone = settings.data?.owner.timezone ?? "UTC";

  const slotsByDay = useMemo(() => {
    const grouped = new Map<string, string[]>();
    for (const slot of availability.data?.slots ?? []) {
      const local = new Date(slot.startUtc).toLocaleString(undefined, {
        timeZone: zone,
        weekday: "short",
        day: "numeric",
        month: "short",
      });
      const time = new Date(slot.startUtc).toLocaleTimeString(undefined, {
        timeZone: zone,
        hour: "2-digit",
        minute: "2-digit",
      });
      grouped.set(local, [...(grouped.get(local) ?? []), time]);
    }
    return [...grouped.entries()];
  }, [availability.data, zone]);

  if (settings.isPending) {
    return (
      <section className="card wide">
        <p className="lede">Loading your availability…</p>
      </section>
    );
  }

  return (
    <section className="card wide">
      <h1>Availability</h1>
      <p className="lede">
        When you&rsquo;re willing to meet. Busy events from your calendars are subtracted
        from these hours.
      </p>

      <h2>Timezone</h2>
      <div className="row">
        <span className="mono">{zone}</span>
        {detectedZone && detectedZone !== zone ? (
          <button
            type="button"
            className="secondary"
            onClick={() => patch.mutate({ timezone: detectedZone })}
          >
            Use {detectedZone}, detected from this device
          </button>
        ) : (
          <span className="helper">Matches this device.</span>
        )}
      </div>

      <h2>Weekly hours</h2>
      {hoursError ? (
        <div className="notice notice-error" role="alert">
          <strong>Those hours weren&rsquo;t saved</strong>
          <span>{hoursError}</span>
        </div>
      ) : null}

      <div className="week">
        {DAYS.map((day) => {
          const ranges = hours.filter((hour) => hour.weekday === day.weekday);
          return (
            <div key={day.weekday} className="weekday">
              <span className="daylabel">{day.label}</span>
              <div className="ranges">
                {ranges.length === 0 ? <span className="helper">Unavailable</span> : null}
                {ranges.map((range, index) => (
                  <div key={`${String(day.weekday)}-${String(index)}`} className="range">
                    <input
                      type="time"
                      value={toTimeValue(range.startMinutes)}
                      onChange={(event) =>
                        setHours((current) =>
                          current.map((hour) =>
                            hour === range
                              ? {
                                  ...hour,
                                  startMinutes: fromTimeValue(event.target.value),
                                }
                              : hour,
                          ),
                        )
                      }
                    />
                    <span>–</span>
                    <input
                      type="time"
                      value={toTimeValue(range.endMinutes)}
                      onChange={(event) =>
                        setHours((current) =>
                          current.map((hour) =>
                            hour === range
                              ? { ...hour, endMinutes: fromTimeValue(event.target.value) }
                              : hour,
                          ),
                        )
                      }
                    />
                    <button
                      type="button"
                      className="ghost"
                      onClick={() =>
                        setHours((current) => current.filter((h) => h !== range))
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="ghost"
                  onClick={() =>
                    setHours((current) => [
                      ...current,
                      { weekday: day.weekday, startMinutes: 9 * 60, endMinutes: 17 * 60 },
                    ])
                  }
                >
                  + range
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="row">
        <button
          type="button"
          className="primary"
          onClick={() => putHours.mutate(hours)}
          disabled={putHours.isPending}
        >
          {putHours.isPending ? "Saving…" : "Save hours"}
        </button>
      </div>

      <h2>Meeting durations offered</h2>
      <div className="row">
        {DURATION_CHOICES.map((duration) => {
          const on = booking?.durations.includes(duration) ?? false;
          return (
            <button
              key={duration}
              type="button"
              className={on ? "chip chip-on" : "chip"}
              aria-pressed={on}
              onClick={() => {
                const current = booking?.durations ?? [];
                const next = on
                  ? current.filter((value) => value !== duration)
                  : [...current, duration];
                if (next.length === 0) return;
                patch.mutate({ durations: next });
              }}
            >
              {duration} min
            </button>
          );
        })}
        <span className="helper">Visitors choose one of these.</span>
      </div>

      <h2>Booking rules</h2>
      <div className="rules">
        <label className="field">
          <span>Minimum notice</span>
          <select
            value={booking?.minNoticeHours ?? 24}
            onChange={(event) =>
              patch.mutate({ minNoticeHours: Number(event.target.value) })
            }
          >
            {[0, 2, 4, 12, 24, 48, 72].map((hours) => (
              <option key={hours} value={hours}>
                {hours === 0 ? "None" : `${String(hours)} hours`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Buffer before</span>
          <select
            value={booking?.bufferBeforeMinutes ?? 10}
            onChange={(event) =>
              patch.mutate({ bufferBeforeMinutes: Number(event.target.value) })
            }
          >
            {[0, 5, 10, 15, 30].map((minutes) => (
              <option key={minutes} value={minutes}>
                {minutes} min
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Buffer after</span>
          <select
            value={booking?.bufferAfterMinutes ?? 10}
            onChange={(event) =>
              patch.mutate({ bufferAfterMinutes: Number(event.target.value) })
            }
          >
            {[0, 5, 10, 15, 30].map((minutes) => (
              <option key={minutes} value={minutes}>
                {minutes} min
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Booking window</span>
          <select
            value={booking?.windowWeeks ?? 6}
            onChange={(event) =>
              patch.mutate({ windowWeeks: Number(event.target.value) })
            }
          >
            {[1, 2, 4, 6, 8, 12].map((weeks) => (
              <option key={weeks} value={weeks}>
                {weeks} {weeks === 1 ? "week" : "weeks"}
              </option>
            ))}
          </select>
        </label>
      </div>

      <h2>Blackouts</h2>
      <p className="helper">
        Time you&rsquo;re never bookable, whatever your calendars say. Stored here only —
        nothing is added to Outlook or Google.
      </p>
      <BlackoutList
        blackouts={settings.data?.blackouts ?? []}
        onDelete={(id) => removeBlackout.mutate(id)}
        onAdded={invalidate}
      />

      <h2>What a visitor would see</h2>
      <div className="row">
        {DURATION_CHOICES.map((duration) => (
          <button
            key={duration}
            type="button"
            className={previewDuration === duration ? "chip chip-on" : "chip"}
            aria-pressed={previewDuration === duration}
            onClick={() => setPreviewDuration(duration)}
          >
            {duration} min
          </button>
        ))}
        <button
          type="button"
          className="ghost"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
        >
          {refresh.isPending ? "Refreshing calendars…" : "Refresh calendars"}
        </button>
      </div>

      {refresh.data ? (
        <div className="notice" role="status">
          <strong>Calendars refreshed</strong>
          <span>
            {refresh.data.results
              .map((result) =>
                result.error
                  ? `${result.organization}: ${result.error}`
                  : `${result.organization}: ${String(result.events ?? 0)} events`,
              )
              .join(" · ")}
          </span>
        </div>
      ) : null}

      {availability.data ? (
        <>
          <p className="helper">
            {availability.data.slots.length} slots over the next 7 days, shown in {zone}.
            {availability.data.holidays.length > 0
              ? ` Federal holidays removed: ${availability.data.holidays.map((h) => h.name).join(", ")}.`
              : ""}
          </p>
          <div className="preview">
            {slotsByDay.length === 0 ? (
              <span className="helper">Nothing open in the next 7 days.</span>
            ) : null}
            {slotsByDay.map(([day, times]) => (
              <div key={day} className="previewday">
                <strong>{day}</strong>
                <span className="mono">
                  {times.slice(0, 8).join("  ")}
                  {times.length > 8 ? ` +${String(times.length - 8)} more` : ""}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}

function BlackoutList({
  blackouts,
  onDelete,
  onAdded,
}: {
  blackouts: Blackout[];
  onDelete: (id: string) => void;
  onAdded: () => void;
}) {
  const [label, setLabel] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const add = useMutation({
    mutationFn: () =>
      addBlackout({
        label,
        kind: "once",
        allDay: false,
        startsAt: new Date(start).toISOString(),
        endsAt: new Date(end).toISOString(),
        weekdays: [],
      }),
    onSuccess: () => {
      setLabel("");
      setStart("");
      setEnd("");
      onAdded();
    },
  });

  return (
    <>
      {blackouts.length === 0 ? <p className="helper">No blackouts yet.</p> : null}
      {blackouts.map((blackout) => (
        <div key={blackout.id} className="account-head">
          <div className="account-id">
            <strong>{blackout.label}</strong>
            <span className="mono">
              {new Date(blackout.startsAt).toLocaleString()} –{" "}
              {new Date(blackout.endsAt).toLocaleString()}
            </span>
          </div>
          <span className="count">{blackout.kind}</span>
          <button type="button" className="ghost" onClick={() => onDelete(blackout.id)}>
            Delete
          </button>
        </div>
      ))}

      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate();
        }}
      >
        <input
          type="text"
          placeholder="Conference — Berlin"
          value={label}
          required
          onChange={(event) => setLabel(event.target.value)}
        />
        <input
          type="datetime-local"
          value={start}
          required
          onChange={(event) => setStart(event.target.value)}
        />
        <input
          type="datetime-local"
          value={end}
          required
          onChange={(event) => setEnd(event.target.value)}
        />
        <button type="submit" className="secondary" disabled={add.isPending}>
          Add blackout
        </button>
      </form>
    </>
  );
}
